import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ClipboardList, ShoppingCart } from "lucide-react";
import purchaseRequestService from "../../services/purchaseRequestService";
import employeeService from "../../services/employeeService";
import financeService from "../../services/financeService";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import ProcurementRequestForm from "./ProcurementRequestForm";

const PurchaseRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  // Check if in edit mode
  const editMode = location.state?.editMode || false;
  const requestData = location.state?.requestData || null;

  // Request-type chooser - only shown when creating a new request. Existing
  // requests being edited are always "purchase" type (Procurement Request is
  // new, nothing to edit yet), so edit mode always uses this form.
  const [requestType, setRequestType] = useState("purchase");

  const [formData, setFormData] = useState({
    requester_name: requestData?.requester_name || "",
    requester_email: requestData?.requester_email || user?.email || "",
    department: requestData?.department || "",
    subsidiary_id: requestData?.subsidiary_id || "",
    item_service_requested: requestData?.item_service_requested || "",
    unit_price: requestData?.unit_price || "",
    quantity: requestData?.quantity || 1,
    amount: requestData?.amount || "",
    vendor_name: requestData?.vendor_name || "",
    vendor_bank_details: requestData?.vendor_bank_details || "",
    payment_method: requestData?.payment_method || "transfer",
    priority: requestData?.priority || "medium",
    notes: requestData?.notes || "",
    selected_manager: requestData?.approving_manager_id || "",
  });
  const [supportingDocument, setSupportingDocument] = useState(null);
  const [existingDocumentUrl, setExistingDocumentUrl] = useState(
    requestData?.cdn_url || requestData?.supporting_document_path || null,
  );
  const [errors, setErrors] = useState({});
  const [hrDepartments, setHrDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loadingSubsidiaries, setLoadingSubsidiaries] = useState(false);
  const [settings, setSettings] = useState({});

  // Load HR departments on component mount
  useEffect(() => {
    loadHRDepartments();
    loadManager();
    loadSubsidiaries();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await purchaseRequestService.getSettings();
      setSettings(response.data.data?.settings || {});
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  // Update requester email when user data loads
  useEffect(() => {
    if (user?.email && !formData.requester_email) {
      setFormData((prev) => ({
        ...prev,
        requester_email: user.email,
      }));
    }
  }, [user]);

  const loadHRDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const response = await employeeService.getDepartments();
      const departments =
        response.data.data?.departments || response.data.departments || [];

      // Transform departments for the form
      const transformedDepartments = departments.map((dept) => ({
        value: dept.code || dept.name.toLowerCase().replace(/\s+/g, "_"),
        label: dept.name,
        id: dept.id,
      }));

      setHrDepartments(transformedDepartments);
      console.log("Loaded HR departments:", transformedDepartments);
    } catch (error) {
      console.error("Load HR departments error:", error);
      // Fallback to default departments
      const fallbackDepartments = [
        { value: "operations_1", label: "Operations 1", id: 1 },
        { value: "operations_2", label: "Operations 2", id: 2 },
        { value: "admin", label: "Admin", id: 3 },
        { value: "farm_manager", label: "Farm Manager", id: 4 },
      ];
      setHrDepartments(fallbackDepartments);
      toast.error("Failed to load departments. Using default departments.");
    } finally {
      setLoadingDepartments(false);
    }
  };

  const loadSubsidiaries = async () => {
    try {
      setLoadingSubsidiaries(true);
      const response = await financeService.getSubsidiaries();
      console.log("Subsidiaries API response:", response);
      console.log("Subsidiaries data:", response.data);

      let subsidiariesData = response.data.data || response.data || [];

      // If it's an object with subsidiaries property, extract it
      if (!Array.isArray(subsidiariesData) && subsidiariesData.subsidiaries) {
        subsidiariesData = subsidiariesData.subsidiaries;
      }

      // If still an object, convert to array
      if (
        !Array.isArray(subsidiariesData) &&
        typeof subsidiariesData === "object"
      ) {
        subsidiariesData = Object.values(subsidiariesData);
      }

      console.log("Parsed subsidiaries:", subsidiariesData);
      setSubsidiaries(Array.isArray(subsidiariesData) ? subsidiariesData : []);
    } catch (error) {
      console.error("Error loading subsidiaries:", error);
      setSubsidiaries([]);
      toast.error("Failed to load subsidiaries");
    } finally {
      setLoadingSubsidiaries(false);
    }
  };

  const loadManager = async () => {
    try {
      const managersResponse = await purchaseRequestService.getManagers();
      const dbManagers = managersResponse.data.data?.managers || [];

      // Transform managers to have the correct structure for the select field
      const transformedManagers = dbManagers.map((manager) => ({
        id: manager.id,
        value: manager.id.toString(), // Use ID as value
        label: `${manager.name} - ${manager.department_name || manager.department}`,
        name: manager.name,
        position:
          manager.position || manager.department_name || manager.department,
        department: manager.department_name || manager.department,
      }));

      console.log("Loaded and transformed managers:", transformedManagers);
      setSelectedManagers(transformedManagers);
    } catch (error) {
      console.error("Load managers error:", error);
      toast.error("Failed to load managers.");
    } finally {
      setLoadingDepartments(false);
    }
  };

  const priorities = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  const managers = selectedManagers;

  // Handle department change - clear manager selection to force user choice
  const handleDepartmentChange = (department) => {
    setFormData((prev) => ({
      ...prev,
      department: department,
      selected_manager: "", // Clear manager selection to force user to choose
    }));

    // Clear manager error when department changes
    if (errors.selected_manager) {
      setErrors((prev) => ({
        ...prev,
        selected_manager: "",
      }));
    }
  };

  // Get threshold from settings (default 1,000,000)
  const getThreshold = () => {
    return settings.procurement_threshold || 1000000;
  };

  const threshold = getThreshold();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for department change
    if (name === "department") {
      handleDepartmentChange(value);
    } else {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [name]: value,
        };

        // Auto-calculate amount when unit_price or quantity changes
        if (name === "unit_price" || name === "quantity") {
          const unitPrice =
            parseFloat(name === "unit_price" ? value : prev.unit_price) || 0;
          const quantity =
            parseFloat(name === "quantity" ? value : prev.quantity) || 1;
          updated.amount = (unitPrice * quantity).toFixed(2);
        }

        return updated;
      });
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error(
          "Only PDF, DOC, DOCX, JPG, PNG, XLS, XLSX files are allowed",
        );
        return;
      }

      setSupportingDocument(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.requester_name.trim()) {
      newErrors.requester_name = "Requester name is required";
    }

    if (!formData.requester_email.trim()) {
      newErrors.requester_email = "Requester email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.requester_email)) {
      newErrors.requester_email = "Please enter a valid email address";
    }

    if (!formData.department) {
      newErrors.department = "Department is required";
    }

    if (!formData.subsidiary_id && !editMode) {
      newErrors.subsidiary_id = "Subsidiary is required";
    }

    if (!formData.item_service_requested.trim()) {
      newErrors.item_service_requested = "Item/Service description is required";
    } else if (formData.item_service_requested.length < 10) {
      newErrors.item_service_requested =
        "Description must be at least 10 characters";
    }

    if (!formData.unit_price) {
      newErrors.unit_price = "Unit price is required";
    } else if (parseFloat(formData.unit_price) <= 0) {
      newErrors.unit_price = "Unit price must be greater than zero";
    }

    if (!formData.quantity) {
      newErrors.quantity = "Quantity is required";
    } else if (parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = "Quantity must be greater than zero";
    }

    if (!formData.amount) {
      newErrors.amount = "Amount is required";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than zero";
    }

    if (!formData.vendor_name.trim()) {
      newErrors.vendor_name = "Vendor name is required";
    }

    // Validate vendor bank details only for transfer payments
    if (formData.payment_method === 'transfer') {
      if (!formData.vendor_bank_details.trim()) {
        newErrors.vendor_bank_details = "Vendor bank details are required for transfer payments";
      } else if (formData.vendor_bank_details.length < 20) {
        newErrors.vendor_bank_details =
          "Bank details must include bank name, account number, account name";
      }
    }

    if (!formData.selected_manager) {
      newErrors.selected_manager =
        "Please select a manager to approve this request";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setLoading(true);

    try {
      if (editMode && requestData) {
        // Update existing request
        const updateData = {
          department: formData.department,
          item_service_requested: formData.item_service_requested,
          amount: formData.amount,
          vendor_name: formData.vendor_name,
          vendor_bank_details: formData.vendor_bank_details,
          priority: formData.priority,
          notes: formData.notes,
          approving_manager_id: formData.selected_manager,
        };

        await purchaseRequestService.updateRequest(
          requestData.request_id,
          updateData,
        );
        toast.success("Purchase request updated successfully!");
        navigate("/purchase-requests/create");
      } else {
        // Create new request
        const submitData = new FormData();

        // Append form data with field name mapping
        Object.keys(formData).forEach((key) => {
          // Map selected_manager to approving_manager_id for backend compatibility
          if (key === "selected_manager") {
            submitData.append("approving_manager_id", formData[key]);
            console.log(
              "Mapped selected_manager to approving_manager_id:",
              formData[key],
            );
          } else {
            submitData.append(key, formData[key]);
          }
        });

        // Log what we're sending
        console.log("Submitting purchase request with data:");
        for (let [key, value] of submitData.entries()) {
          if (key !== "vendor_bank_details") {
            console.log(`  ${key}:`, value);
          }
        }

        // Append file if selected
        if (supportingDocument) {
          submitData.append("supporting_document", supportingDocument);
        }

        const response = await purchaseRequestService.createRequest(submitData);

        toast.success("Purchase request created successfully!");

        // Reset form
        setFormData({
          requester_name: user?.first_name + " " + user?.last_name || "",
          department: user?.department || "",
          item_service_requested: "",
          vendor_name: "",
          vendor_bank_details: "",
          amount: "",
          priority: "medium",
          justification: "",
          selected_manager: "",
        });
        setSupportingDocument(null);

        // Navigate back to create page to show updated list
        navigate("/purchase-requests/create");
      }
    } catch (error) {
      console.error("Purchase request error:", error);
      toast.error(
        error.response?.data?.message ||
          `Failed to ${editMode ? "update" : "create"} purchase request`,
      );
    } finally {
      setLoading(false);
    }
  };

  // Switching to Procurement Request renders a completely separate form -
  // this is placed after every hook above so it never affects hook order.
  if (!editMode && requestType === "procurement") {
    return <ProcurementRequestForm onBack={() => setRequestType("purchase")} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">
                  {editMode
                    ? "✏️ Edit Purchase Request"
                    : "📝 Create Purchase Request"}
                </h1>
                <p className="text-blue-100 mt-2">
                  {editMode
                    ? `Updating request ${requestData?.request_id}`
                    : "Fill in the details below to submit your request"}
                </p>
              </div>
              {editMode && (
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <p className="text-white text-sm font-medium">
                    {requestData?.request_id}
                  </p>
                </div>
              )}
            </div>
          </div>
          {!editMode && (
            <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-2">REQUEST TYPE</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setRequestType("purchase")}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-blue-500 bg-blue-50 text-left"
                >
                  <ClipboardList className="h-5 w-5 text-blue-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Purchase Request</p>
                    <p className="text-xs text-gray-500">Single item, quick submission</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setRequestType("procurement")}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 text-left"
                >
                  <ShoppingCart className="h-5 w-5 text-gray-500 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Procurement Request</p>
                    <p className="text-xs text-gray-500">Multiple items, budget coding, vendor selection</p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Requester Information Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-blue-600 text-xl">👤</span>
              </div>
              <h2 className="text-xl font-bold text-gray-800">
                Requester Information
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="requester_name"
                  value={formData.requester_name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.requester_name
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.requester_name && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <span className="mr-1">⚠️</span> {errors.requester_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="requester_email"
                  value={formData.requester_email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.requester_email
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter email address"
                />
                {errors.requester_email && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.requester_email}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department *
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  disabled={loadingDepartments}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.department ? "border-red-500" : "border-gray-300"
                  } ${loadingDepartments ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <option value="">
                    {loadingDepartments
                      ? "Loading departments..."
                      : "Select Department"}
                  </option>
                  {hrDepartments.map((dept) => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
                {errors.department && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.department}
                  </p>
                )}
                {loadingDepartments && (
                  <p className="text-blue-500 text-sm mt-1">
                    Loading departments from HR system...
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  Select your department from the HR system
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subsidiary *
                </label>
                <select
                  name="subsidiary_id"
                  value={formData.subsidiary_id}
                  onChange={handleInputChange}
                  disabled={loadingSubsidiaries}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.subsidiary_id ? "border-red-500" : "border-gray-300"
                  } ${loadingSubsidiaries ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <option value="">
                    {loadingSubsidiaries
                      ? "Loading subsidiaries..."
                      : "Select Subsidiary"}
                  </option>
                  {subsidiaries.map((subsidiary) => (
                    <option key={subsidiary.id} value={subsidiary.id}>
                      {subsidiary.name}
                    </option>
                  ))}
                </select>
                {errors.subsidiary_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.subsidiary_id}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  Select the subsidiary for proper financial reporting
                </p>
              </div>
            </div>

            {/* Request Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Item/Service Requested *
              </label>
              <textarea
                name="item_service_requested"
                value={formData.item_service_requested}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.item_service_requested
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Describe the item or service you are requesting in detail..."
              />
              {errors.item_service_requested && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.item_service_requested}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                {formData.item_service_requested.length}/2000 characters
              </p>
            </div>

            {/* Manager Selection - Required user selection */}
            {formData.department && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Approving Manager *
                </label>
                {(() => {
                  return (
                    <div>
                      <select
                        name="selected_manager"
                        value={formData.selected_manager}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.selected_manager
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      >
                        <option value="">
                          Choose a manager to approve this request
                        </option>
                        {managers.map((manager) => (
                          <option key={manager.value} value={manager.value}>
                            {manager.name}{" "}
                            {manager.position ? `(${manager.position})` : ""}
                          </option>
                        ))}
                      </select>

                      {/* Show selected manager details */}
                      {formData.selected_manager &&
                        (() => {
                          const selectedManager = managers.find(
                            (m) => m.value === formData.selected_manager,
                          );
                          return selectedManager ? (
                            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <span className="text-green-600">✓</span>
                                </div>
                                <div className="ml-2">
                                  <p className="text-sm font-medium text-green-900">
                                    Selected: {selectedManager.label}
                                  </p>
                                  <p className="text-xs text-green-700">
                                    {selectedManager.position} •{" "}
                                    {selectedManager.value}
                                  </p>
                                  <p className="text-xs text-green-600 mt-1">
                                    This manager will receive the approval
                                    request
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : null;
                        })()}

                      <p className="text-gray-500 text-sm mt-1">
                        You must select a manager who will approve this purchase
                        request
                      </p>
                    </div>
                  );
                })()}
                {errors.selected_manager && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.selected_manager}
                  </p>
                )}
              </div>
            )}

            {/* Unit Price, Quantity, and Total Amount */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit Price (NGN) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">₦</span>
                  <input
                    type="number"
                    name="unit_price"
                    value={formData.unit_price}
                    onChange={handleInputChange}
                    min="1"
                    step="0.01"
                    className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.unit_price ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="0.00"
                  />
                </div>
                {errors.unit_price && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.unit_price}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  Price per unit/item
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity *
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  min="1"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.quantity ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="1"
                />
                {errors.quantity && (
                  <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>
                )}
                <p className="text-gray-500 text-sm mt-1">Number of units</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Amount (NGN)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">₦</span>
                  <input
                    type="text"
                    name="amount"
                    value={
                      formData.amount
                        ? parseFloat(formData.amount).toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "0.00"
                    }
                    readOnly
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed text-gray-700 font-medium"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Auto-calculated: ₦{formData.unit_price || 0} ×{" "}
                  {formData.quantity || 1}
                </p>
                {formData.amount &&
                  parseFloat(formData.amount) >= threshold && (
                    <p className="text-blue-600 text-xs mt-1">
                      ⚠️ Requires procurement review
                    </p>
                  )}
              </div>
            </div>

            {/* Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Vendor Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Name *
                </label>
                <input
                  type="text"
                  name="vendor_name"
                  value={formData.vendor_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.vendor_name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter vendor/supplier name"
                />
                {errors.vendor_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.vendor_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method *
                </label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="transfer">Bank Transfer</option>
                  <option value="cash">Cash Payment</option>
                </select>
                <p className="text-gray-500 text-sm mt-1">
                  How will this vendor be paid?
                </p>
              </div>
            </div>

            {/* Supporting Document */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supporting Document
              </label>
              {existingDocumentUrl && !supportingDocument && (
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    ✓ Existing document:
                    <a
                      href={existingDocumentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 underline"
                    >
                      View Document
                    </a>
                  </p>
                </div>
              )}
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-gray-500 text-sm mt-1">
                {editMode
                  ? "Optional. Upload new document to replace existing one."
                  : "Optional. Max 10MB. Allowed: PDF, DOC, DOCX, JPG, PNG, XLS, XLSX"}
              </p>
              {supportingDocument && (
                <p className="text-green-600 text-sm mt-1">
                  ✓ {supportingDocument.name} selected (will replace existing)
                </p>
              )}
            </div>

            {/* Vendor Bank Details - Only show for transfer payments */}
            {formData.payment_method === 'transfer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Bank Details *
                </label>
                <textarea
                  name="vendor_bank_details"
                  value={formData.vendor_bank_details}
                  onChange={handleInputChange}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.vendor_bank_details
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Bank Name, Account Number, Account Name"
                />
                {errors.vendor_bank_details && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.vendor_bank_details}
                  </p>
                )}
              </div>
            )}

            {/* Cash Payment Notice */}
            {formData.payment_method === 'cash' && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <span className="text-green-600 text-lg">💰</span>
                  <p className="text-green-800 font-medium">Cash Payment Selected</p>
                </div>
                <p className="text-green-700 text-sm mt-1">No bank details required for cash payments</p>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-300 transition-all"
                placeholder="Any additional information or special instructions..."
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate("/purchase-requests/create")}
                className="px-8 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
              >
                ← Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all transform hover:scale-105"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {editMode ? "Updating..." : "Creating..."}
                  </div>
                ) : (
                  <span className="flex items-center">
                    {editMode ? "✓ Update Request" : "✓ Submit Request"}
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseRequestForm;
